import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { RegisterInforService } from '@pg-app/auth/services/register-infor.service';
import { RegisterStep1Service } from '@pg-app/auth/services/register-step-1.service';
import { environment } from '@pg-env/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@pg-app/core/services/auth.service';
import { CommonMessages } from '@pg-app/shared/constants/common-messages';
import { ShareService } from '@nttp/common-service/share-service';
import { RecaptchaComponent } from 'ng-recaptcha';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { LeadType, LeadTypeString } from '@nttp/enum/lead-type.enum';
import { CRMAccountType } from '@nttp/enum/account-type.enum';

function compareEmail(c: AbstractControl) {
  const v = c.value;
  return v.email.toLowerCase() === v.confirmEmail.toLowerCase()
    ? null
    : {
        emailnotmatch: true
      };
}

function comparePin(c: AbstractControl) {
  const v = c.value;
  return v.pinCode === v.confirmPin
    ? null
    : {
        pinnotmatch: true
      };
}

function comparePass(c: AbstractControl) {
  const v = c.value;
  return v.password === v.confirmPass
    ? null
    : {
        passnotmatch: true
      };
}

export interface CommonModel {
  name: string;
  id: string;
}

@Component({
  selector: 'np-register-corporate',
  templateUrl: './register-corporate.component.html',
  styleUrls: ['./register-corporate.component.scss']
})
export class RegisterCorporateComponent implements OnInit {
  @ViewChild('captchaRef', { static: true })
  recaptcha: RecaptchaComponent;

  individualForm: FormGroup;
  customerType = '';
  wrongPin: boolean;
  destroy$ = new Subject<boolean>();
  passwordPattern = '^(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[(!@#$%^&*()_+|~ ={}[:”;<>?,., )]).{8,}$';
  // user name can contains characters (a-z, A-Z, 0-9, dash -,  apostrophe ', space), no special characters
  namePattern = `([A-Za-z0-9\-' ]+)`;
  countries: Array<any> = [];
  titles: CommonModel[] = [];
  countryDefault: any;
  emailDefault: string;
  acceptPolicy = false;
  acceptMarketingEmails = false;
  submitForm = false;
  partnerResidencies = [];
  isVATRegistered = false;
  recaptchaV2SiteKey = environment.recaptchaV2SiteKey;
  recaptchaToken = '';
  commonMessages = CommonMessages;
  showResendEmailBox = false;
  resendEmailInfo: any;
  isPrivalgoMarkets = environment.isPrivalgoMarkets;
  domain = environment.domain;
  brandName = environment.brandName;

  get titleICtrl(): FormControl {
    return this.individualForm.get('title') as FormControl;
  }
  get firstNameICtrl(): FormControl {
    return this.individualForm.get('firstName') as FormControl;
  }
  get middleNameICtrl(): FormControl {
    return this.individualForm.get('middleName') as FormControl;
  }
  get lastNameICtrl(): FormControl {
    return this.individualForm.get('lastName') as FormControl;
  }
  get emailICtrl(): FormControl {
    return this.individualForm.get('email') as FormControl;
  }
  get confirmEmailICtrl(): FormControl {
    return this.individualForm.get('confirmEmail') as FormControl;
  }
  get pinCodeICtrl(): FormControl {
    return this.individualForm.get('pinCode') as FormControl;
  }
  get confirmPinICtrl(): FormControl {
    return this.individualForm.get('confirmPin') as FormControl;
  }
  get passwordICtrl(): FormControl {
    return this.individualForm.get('password') as FormControl;
  }
  get confirmPassICtrl(): FormControl {
    return this.individualForm.get('confirmPass') as FormControl;
  }

  constructor(
    private fb: FormBuilder,
    private registerStep1Service: RegisterStep1Service,
    private router: Router,
    private registerInfor: RegisterInforService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private authService: AuthService,
    private shareService: ShareService,
  ) {
    const tokenId = this.route.snapshot.queryParams['token'];
    const title = this.route.snapshot.queryParams['title'];
    const countryCode = this.route.snapshot.queryParams['countrycode'];
    this.emailDefault = this.route.snapshot.queryParams['email'];
    this.customerType = this.route.snapshot.queryParams['customertype'];

    if (tokenId) {
      this.checkToken(tokenId);
    }

    this.getTitles(title);
    this.getPartnerResidency();
    if (countryCode) {
      this.getCountries(countryCode);
    }
  }

  ngOnInit() {
    this.individualForm = this.fb.group(
      {
        title: ['', Validators.required],
        firstName: ['', [Validators.required, Validators.pattern(this.namePattern), Validators.maxLength(255)]],
        middleName: ['', [Validators.pattern(this.namePattern), Validators.maxLength(255)]],
        lastName: ['', [Validators.required, Validators.pattern(this.namePattern), Validators.maxLength(255)]],
        email: ['', [Validators.required, Validators.email]],
        confirmEmail: '',
        pinCode: ['', [Validators.required, Validators.minLength(6)]],
        confirmPin: '',
        password: ['', [Validators.required, Validators.pattern(this.passwordPattern)]],
        confirmPass: '',
        partnerResidency: '',
        vatNumber: '',
        vatCountry: '',
        recaptchaReactive: [null, Validators.required]
      },
      {
        validator: [compareEmail, comparePin, comparePass]
      }
    );
    if (this.emailDefault) {
      this.individualForm.get('email').setValue(this.emailDefault);
      this.individualForm.get('confirmEmail').setValue(this.emailDefault);
    }
    this.pinCodeICtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.checkPin(value);
    });
  }

  checkToken(tokenId) {
    const params = {
      token: tokenId,
      ticketType: 4
    };
    this.registerStep1Service.validateToken(params).subscribe(
      res => {
        this.getRegisterInfo(tokenId);
      },
      err => {
        const responseData = err.error.responseData;
        if (responseData && responseData.requireResend) {
          this.showResendEmailBox = true;
          this.resendEmailInfo = responseData.resendInfo;
        }
        else {
          this.authService.logout();
        }
      }
    );
  }

  individualSubmit() {
    this.submitForm = true;
    this.individualForm.markAllAsTouched();
    if (this.individualForm.valid && !this.wrongPin && this.acceptPolicy) {
      this.createAccount();
    }
  }

  createAccount() {
    if (!this.recaptchaToken) {
      return;
    }

    const email = this.individualForm.get('email').value as string;
    const title = this.titleICtrl.value;

    const individualAccount = {
      title: title
        ? {
            titleId: title.id,
            title: title.name
          }
        : null,
      titleId: this.titleICtrl.value.id,
      firstName: this.upperCaseName(this.firstNameICtrl.value),
      middleName: this.middleNameICtrl.value ? this.upperCaseName(this.middleNameICtrl.value) : null,
      lastName: this.upperCaseName(this.lastNameICtrl.value),
      email: this.emailICtrl.value,
      confirmEmail: this.confirmEmailICtrl.value,
      pinCode: this.pinCodeICtrl.value,
      password: this.passwordICtrl.value,
      clientId: environment.clientId,
      customerType: this.customerType,
      accountType: CRMAccountType.Lead,
      portalType: 1,
      token: this.route.snapshot.queryParams['token'],
      country: this.countryDefault ? this.countryDefault : null,
      doNotBulkEmail: !this.acceptMarketingEmails,
      partnerResidency: this.individualForm.get('partnerResidency').value
        ? this.individualForm.get('partnerResidency').value
        : null,
      isVATRegistered: this.isVATRegistered,
      vATNumber: this.isVATRegistered ? this.individualForm.get('vatNumber').value : null,
      vATCountryCode: this.isVATRegistered ? this.individualForm.get('vatCountry').value : null,
      recaptchaToken: this.recaptchaToken,
      forceSendEmailRegister: true
    };

    this.registerStep1Service.submitIndividualForm(individualAccount).subscribe(
      res => {
        this.registerInfor.email = email;
        this.registerInfor.userId = res;
        this.router.navigate(['/auth/register/verify-email']);
      },
      err => {
        this.recaptcha.reset();
      }
    );
  }

  checkPin(pin) {
    for (let i = 0; i < pin.length - 2; i++) {
      if (
        (pin.charCodeAt(i) + 1 === pin.charCodeAt(i + 1) && pin.charCodeAt(i) + 2 === pin.charCodeAt(i + 2)) ||
        (pin.charCodeAt(i) === pin.charCodeAt(i + 1) && pin.charCodeAt(i) === pin.charCodeAt(i + 2)) ||
        (pin.charCodeAt(i) - 1 === pin.charCodeAt(i + 1) && pin.charCodeAt(i) - 2 === pin.charCodeAt(i + 2))
      ) {
        this.wrongPin = true;
        return;
      }
    }
    this.wrongPin = false;
  }

  getCountries(countryCode) {
    this.shareService.countryList$.subscribe(
      data => {
        this.countries = data || [];
        this.countryDefault = this.countries.find(item => item.code === countryCode);
      },
    );
  }

  private getTitles(titleName) {
    this.shareService.titleList$.subscribe(
      (data: CommonModel[]) => {
        this.titles = data || [];
        const titleText = titleName ? titleName : 'Mr';
        const defaultTitle = this.titles.find(item => item.name === titleText);
        this.titleICtrl.setValue(defaultTitle);
      },
    );
  }

  onDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public getRegisterInfo(tokenId) {
    this.registerStep1Service.getRegisterInfo(tokenId).subscribe(data => {
      this.individualForm.get('firstName').setValue(data.firstName);
      this.individualForm.get('middleName').setValue(data.middleName);
      this.individualForm.get('lastName').setValue(data.lastName);
      this.individualForm.get('email').setValue(data.email);
      this.individualForm.get('confirmEmail').setValue(data.email);
    });
  }

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  upperCaseName(value) {
    if (value === null) {
      return null;
    }
    const valueSplit = value.split(' ').filter(item => item !== '');
    const name = valueSplit.map(item => {
      return this.capitalizeFirstLetter(item);
    });
    return name.join(' ');
  }

  getPartnerResidency() {
    this.registerStep1Service.getPartnerResidency().subscribe(res => {
      this.partnerResidencies = res;
    });
  }

  resolvedRecaptchaChallenge(captchaResponse: string) {
    this.recaptchaToken = captchaResponse;
  }

  resendEmail() {
    this.registerStep1Service.emailNotArrive(this.resendEmailInfo).subscribe(() => {
      this.dialog.open(CommonDialogComponent, {
        maxWidth: '42vw',
        data: {
          title: "A registration has been sent to your email. Please check the inbox to complete the registration",
          dialogType: 1
        }
      });
    });
  }
}
